import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from "react";
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import PersonIcon from '@mui/icons-material/Person';
import { useForm, FormContainer, FormErrorProvider } from "react-hook-form-mui";
import { useFlow } from "../../context/FlowContext";
import { useNavigate } from "react-router-dom";
import { CustomTextFieldElement } from "../../layouts/Theme";
import PasswordField from "../fields/PasswordField";
import useWidgetStore from "../../store/useWidgetStore";
import { useI18N } from "../../context/I18NContext";
import { useSignin } from "../../hooks/query/useSignin";
import { useTheme } from "@mui/material";
import { useSetConsumer } from "../../hooks/query/useSetConsumer";
import { useSettings } from "../../context/SettingsContext";
export default function SigninForm(props) {
    const flow = useFlow();
    const theme = useTheme();
    const { consumer, isLoginAccount, setConsumer, setIsAccount, setIsGuest, setIsLoginAccount, setIsDirtyAccount, setIsNewAccount, } = useWidgetStore();
    const { forms } = useSettings();
    const [error, setError] = useState(false);
    const navigate = useNavigate();
    const { t } = useI18N();
    const formContextLogin = useForm({});
    const onSubmitLogin = (data, event) => {
        signinQuery.refetch();
    };
    const onErrorLogin = (errors) => {
    };
    const goTo = (e) => {
        let target = e.target.closest('button');
        let path = target.getAttribute('rel');
        navigate(`/oa_widget_${path}`);
    };
    const signinQuery = useSignin(formContextLogin);
    const consumerQuery = useSetConsumer();
    useEffect(() => {
        var _a;
        if (signinQuery.data) {
            if (signinQuery.data.status.status === 'failed') {
                setError(signinQuery.data.status.message);
                formContextLogin.setError('username', { type: "pattern", message: '' }, { shouldFocus: true });
                formContextLogin.setError('password', { type: "value", message: t(signinQuery.data.status.message) });
            }
            else {
                let _consumer = signinQuery.data.result.item;
                if (_consumer.provisional === 2) {
                    setConsumer({
                        id: _consumer.id,
                        hash: _consumer.hash
                    });
                    navigate(`/oa_widget_passwordReset`);
                }
                else {
                    // @ts-ignore
                    let fields = forms[_consumer.type].register.fields, fieldNames = [];
                    Object.values(fields).map((field, index) => {
                        if (field.type === 'stack' && field.fields) {
                            Object.values(field.fields).map((innerfield, indexInner) => {
                                var _a;
                                fieldNames.push((_a = innerfield.name) !== null && _a !== void 0 ? _a : innerfield.label);
                            });
                        }
                        else {
                            fieldNames.push(Object.keys(fields)[index]);
                        }
                    });
                    if (!fieldNames.includes('insertions') && _consumer.insertions !== '') {
                        _consumer.lastName = `${_consumer.insertions} ${_consumer.lastName}`;
                        _consumer.insertions = '';
                    }
                    const consumer = {
                        id: _consumer.id,
                        hash: _consumer.hash,
                        companyId: _consumer.companyId,
                        provisional: 0,
                        firstName: _consumer.firstName,
                        insertions: _consumer.insertions,
                        lastName: _consumer.lastName,
                        email: _consumer.email,
                        address: _consumer.address,
                        houseNumber: _consumer.houseNr,
                        zipcode: _consumer.zipcode,
                        city: _consumer.city,
                        country: (_a = _consumer.country) !== null && _a !== void 0 ? _a : 'nl',
                        type: _consumer.type,
                        fields: {},
                        contracts: []
                    };
                    if (_consumer.address) {
                        // in case we have a split field, the address goes into the street input element
                        consumer.street = _consumer.address;
                    }
                    if (_consumer.phone) {
                        consumer.phone = _consumer.phone;
                    }
                    if (_consumer.mobile) {
                        consumer.mobile = _consumer.mobile;
                    }
                    for (const [_key, _value] of Object.entries(_consumer)) {
                        if (_key.startsWith('custom-field')) {
                            // @ts-ignore
                            consumer.fields[_key] = _value && _value !== 'null' ? _value : '';
                        }
                        else if (_key.startsWith('acceptTerms')) {
                            if (!consumer.contracts.includes('acceptTerms')) {
                                consumer.contracts.push('acceptTerms');
                            }
                        }
                        else if (_key.startsWith('acceptPrivacy')) {
                            if (!consumer.contracts.includes('acceptPrivacy')) {
                                consumer.contracts.push('acceptPrivacy');
                            }
                        }
                    }
                    if (_consumer.type === 'business') {
                        consumer.business = {
                            id: _consumer.business.id,
                            address: _consumer.business.address,
                            zipCode: _consumer.business.zipCode,
                            city: _consumer.business.city,
                            name: _consumer.business.name,
                            vatNumber: _consumer.business.vatNumber,
                        };
                    }
                    if (_consumer.mandate) {
                        consumer.mandate = {
                            id: _consumer.mandate.id,
                            holder: _consumer.mandate.holder,
                            iban: _consumer.mandate.iban,
                            signatureDate: _consumer.mandate.signatureDate,
                            membershipTypes: _consumer.mandate.membershipTypes
                        };
                    }
                    consumer.lastUpdate = signinQuery.data.status.timestamp;
                    setConsumer(consumer);
                    setIsAccount(true);
                    setIsGuest(false);
                    setIsLoginAccount(true);
                    setIsNewAccount(false);
                    setError(false);
                }
            }
        }
    }, [signinQuery.data]);
    useEffect(() => {
        // after the consumer state was updated by the signin query, try to repost the consumer again
        // this can fail, indicating we have to add additional info
        // for example, after I added a new required consumer field, and a new privacy statement,
        // this call failed with these messages:
        //
        // userMessages: 
        //     acceptPrivacy: "You must agree to the processing of your personal data"
        //     custom-field-2070951: "Test nieuw verplicht veld is required"
        // this also handles cases where a field now has different conditions, not just required
        // for example, a number field used to be valid between 1 and 10, and the consumer has 8 
        // after that, the form field was changed to be between 1 and 5, making the 8 invalid
        // this will also trigger a failure, which is nice =)
        if (consumer === null || consumer === void 0 ? void 0 : consumer.email) {
            consumerQuery.refetch();
        }
    }, [consumer === null || consumer === void 0 ? void 0 : consumer.lastUpdate, isLoginAccount]);
    useEffect(() => {
        var _a;
        if (((_a = consumerQuery.data) === null || _a === void 0 ? void 0 : _a.status.status) === 'failed') {
            if (consumer === null || consumer === void 0 ? void 0 : consumer.hash) {
                if (flow.onNext) {
                    // this usually is the accountDetails form ( not accountDetails1 )
                    console.log('go to next page');
                    flow.onNext();
                }
                else {
                    // somehow we could log in, but didn't have a hash, so just go to start as a fallback
                    navigate('/oa_widget_index');
                }
            }
        }
        else if (consumer === null || consumer === void 0 ? void 0 : consumer.hash) {
            setIsDirtyAccount(false);
            // consumer is fine as is, go the the summary
            // btw, this does make the assumption we always want to go to the summary, but in the future
            // this may not be the case
            navigate('/oa_widget_summary');
        }
    }, [consumerQuery.data]);
    useEffect(() => {
        // After we get a hash above, we can start the next request
        // If not, an error has been shown that the customer API call failed
    }, [isLoginAccount]);
    return (_jsx(FormContainer, { formContext: formContextLogin, onSuccess: onSubmitLogin, onError: onErrorLogin, children: _jsxs(FormErrorProvider, { onError: (error) => {
                var _a;
                if (error.type === 'validate') {
                    console.log(error);
                }
                return t((_a = error.message) !== null && _a !== void 0 ? _a : error.type, 'error');
            }, children: [error && typeof (error) === 'string' && (_jsx(Alert, { sx: { my: 1 }, severity: "error", children: t(error) })), _jsx(CustomTextFieldElement, { required: true, label: t('Gebruikersnaam'), name: 'username', type: "input", InputProps: {
                        startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(PersonIcon, { sx: { color: theme.palette.text.secondary } }) }))
                    }, sx: { my: 1 }, fullWidth: true }), _jsx(PasswordField, { label: "Wachtwoord", name: "password" }), _jsx(Button, { type: "submit", fullWidth: true, variant: "contained", children: t('Inloggen') }), _jsx(Button, { onClick: goTo, rel: 'passwordReminder1', fullWidth: true, sx: {
                        mt: 2,
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText,
                        borderColor: theme.palette.mode === 'light'
                            ? theme.palette.secondary.dark
                            : theme.palette.secondary.light,
                        "&:hover": {
                            backgroundColor: theme.palette.mode === 'light'
                                ? theme.palette.secondary.dark
                                : theme.palette.secondary.light,
                            color: theme.palette.secondary.contrastText
                        }
                    }, variant: "outlined", children: t('Wachtwoord of gebruikersnaam vergeten?') })] }) }));
}
